import React, { useEffect, useRef } from "react";
import { Sparkles, Users, Truck, Globe, Megaphone, BadgeCheck, Zap } from "lucide-react";
import { trackEvent } from "@/lib/retention";

/**
 * Phase 11.5 — Why People Choose Lifted.
 *
 * Factual statements only — NO income claims, NO unrealistic promises.
 * Fires `why_lifted_view` once when scrolled into view.
 */
const REASONS = [
  { icon: Sparkles,  title: "Growing product catalog",        blurb: "Capsules, accessories, supplies — always expanding." },
  { icon: Globe,     title: "Nationwide customer reach",      blurb: "Real customers, real cities, growing every month." },
  { icon: Users,     title: "Community-driven growth",        blurb: "Powered by people who love the brand." },
  { icon: BadgeCheck,title: "Affiliate opportunities",        blurb: "Tier-based program. Earn on qualifying referrals." },
  { icon: Truck,     title: "Same-day delivery expansion",    blurb: "Lifted Supplies network rolling out city by city." },
  { icon: Megaphone, title: "Content creator opportunities",  blurb: "Creators build awareness, partners get exposure." },
  { icon: Zap,       title: "Brand-focused ecosystem",        blurb: "One brand. Many roles. All connected." },
];

export default function WhyChooseLiftedSection({ id = "home-why-lifted" }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current || typeof window === "undefined") return;
    if (!("IntersectionObserver" in window)) {
      trackEvent("why_lifted_view");
      return;
    }
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          trackEvent("why_lifted_view");
          io.disconnect();
          break;
        }
      }
    }, { threshold: 0.3 });
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);

  return (
    <section
      id={id}
      ref={ref}
      data-testid={id}
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16"
    >
      <div className="text-center max-w-2xl mx-auto fade-up">
        <div className="eyebrow inline-flex items-center gap-2 justify-center"><Sparkles className="h-3 w-3" /> Why Lifted</div>
        <h2 className="font-anton text-4xl sm:text-5xl uppercase mt-3">Why people<br /><span className="text-lifted-green">choose Lifted.</span></h2>
        <p className="text-lifted-dim text-sm mt-4">
          Facts, not promises. Here's what the LiftedAtTheMoment ecosystem actually offers today.
        </p>
      </div>
      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {REASONS.map((r) => {
          const Icon = r.icon;
          return (
            <div
              key={r.title}
              data-testid={`why-lifted-${r.title.split(" ")[0].toLowerCase()}`}
              className="glass-panel p-5"
            >
              <div className="h-9 w-9 rounded-full bg-lifted-green/15 border border-lifted-green/30 grid place-items-center text-lifted-green">
                <Icon className="h-4 w-4" />
              </div>
              <div className="font-anton uppercase mt-3 text-base">{r.title}</div>
              <div className="text-[0.72rem] text-lifted-dim mt-1.5 leading-relaxed">{r.blurb}</div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
